/**
 * An identity home's sign-in address (TECH-3350,
 * docs/adr/33-identity-homes-sign-in-chosen-by-the-address.md).
 *
 * The label is the FIRST PATH SEGMENT for now — `<base host>/acme` — and only
 * this page lives there. The app itself runs at its usual paths, so nothing else
 * in the client knows the label exists, and TECH-3364 moves it in front of the
 * host without touching anything here but the address this page reads.
 *
 * It asks the server which Organisation the address names BEFORE it renders
 * anything, because the answer decides both what the form is called and which
 * pool of Identities it signs into. *No probing* in the record is why the
 * refusal says so little: an address that names nothing, a switched-off
 * Organisation and one whose pool is not made yet are one answer here.
 *
 * Web only in practice. The desktop build routes on the hash and has no address
 * of its own, so it keeps the login at `/` (see `routes/index.tsx`).
 */
import { createFileRoute } from "@tanstack/react-router";
import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { PASSWORD_SET_HANDOVER_KEY } from "@/components/auth/set-password-page";
import {
	NO_IDENTITY_HOME_MESSAGE,
	SIGN_IN_UNAVAILABLE_MESSAGE,
	type SignedOut,
	SignInPage,
	validateRedirectSearch,
} from "@/components/auth/sign-in-page";
import { type IdentityHomeResolution, resolveIdentityHome } from "@/lib/api";
import { type Destination, scopeOfDestination } from "@/lib/destination";
import { setSignInTenant } from "@/lib/firebase";

export const Route = createFileRoute("/$label")({
	validateSearch: validateRedirectSearch,
	component: LabelSignInComponent,
});

/** What the form says it is for, under the Organisation's name, by destination. */
const SCOPE_DESCRIPTION = {
	app: "Enter your credentials to access your account",
	operations: "Sign in to the operations portal",
	organization: "Sign in to manage your organization",
} as const;

const descriptionOf = (destination: Destination | undefined): string =>
	destination === "/instance"
		? "Sign in to administer this ELASO Instance"
		: SCOPE_DESCRIPTION[scopeOfDestination(destination)];

function LabelSignInComponent() {
	// Only to notice that the address now names a DIFFERENT Organisation: the
	// router keeps this page mounted when nothing but the parameter changes. The
	// label's value is still never interpreted here.
	const { label } = Route.useParams();
	const { redirect } = Route.useSearch();
	// `null` is "not asked yet". Collapsing it into a refusal would flash "this
	// address names no Organisation" at every member of every Organisation on
	// every load, which is the one thing this page must never say by accident.
	const [resolution, setResolution] = useState<IdentityHomeResolution | null>(
		null
	);
	// The email the set-password page left on its way here (TECH-3456). Read
	// once, then cleared, so a reload shows the ordinary form; held in a tab's
	// sessionStorage rather than the URL, where it would be logged and shared.
	const [passwordSetEmail] = useState(() =>
		sessionStorage.getItem(PASSWORD_SET_HANDOVER_KEY)
	);

	useEffect(() => {
		let live = true;
		// Back to the loader first. Holding the previous answer while this one is
		// in flight would show one Organisation's name over a form pointed at the
		// other's pool.
		setResolution(null);
		// The WHOLE address, as the browser holds it. Which part of it is the label
		// is the server's business (TECH-3364 moves it), and this page interprets
		// none of it.
		resolveIdentityHome(window.location.href).then((result) => {
			if (!live) {
				return;
			}
			if (result.ok) {
				// BEFORE the form exists, so no credential can be entered against a
				// session that is still pointed at the project-wide pool.
				setSignInTenant(result.home.gcipTenantId);
			}
			setResolution(result);
		});
		return () => {
			live = false;
		};
	}, [label]);

	// Once the address has answered: the toast says to sign in, so it shows only
	// with the form, and the email is done with either way.
	useEffect(() => {
		if (!resolution) {
			return;
		}
		if (resolution.ok && passwordSetEmail) {
			// One id, so the effect running twice in development shows one toast.
			toast.success("Your password is set. Sign in with it now.", {
				id: "password-set",
			});
		}
		sessionStorage.removeItem(PASSWORD_SET_HANDOVER_KEY);
	}, [resolution, passwordSetEmail]);

	if (!resolution) {
		return (
			<div className="flex min-h-screen items-center justify-center p-4">
				<Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
			</div>
		);
	}

	return (
		<SignInPage
			destination={redirect}
			signedOut={signedOutFor(resolution, redirect, passwordSetEmail)}
		/>
	);
}

const signedOutFor = (
	resolution: IdentityHomeResolution,
	destination: Destination | undefined,
	passwordSetEmail: string | null
): SignedOut => {
	if (resolution.ok) {
		// Titled with the Organisation's name, which is the one thing that tells
		// somebody they are signing in at the right place.
		return {
			form: {
				title: resolution.home.displayName,
				description: descriptionOf(destination),
				...(passwordSetEmail ? { email: passwordSetEmail } : {}),
			},
		};
	}
	// Two different lines. An outage is not a fact about any Organisation, and a
	// visitor told "this address names nothing" during one would go looking for an
	// address they already have.
	return {
		message:
			resolution.reason === "not_found"
				? NO_IDENTITY_HOME_MESSAGE
				: SIGN_IN_UNAVAILABLE_MESSAGE,
	};
};
